/**
 * Service worker registration that never throws.
 * Some browsers, crawlers, and privacy tools resolve `register()` with
 * `undefined` instead of a Registration, which previously crashed on
 * `registration.scope`.
 */
import { visibilityManager } from './visibilityManager';

export function logServiceWorkerRegistration(
  registration: { scope?: unknown } | null | undefined
): void {
  try {
    const scope = registration?.scope;
    if (typeof scope === 'string' && scope.length > 0) {
      console.log('ServiceWorker registration successful:', scope);
    }
  } catch (error) {
    console.log('ServiceWorker registration logging skipped:', error);
  }
}

let activeRegistration: ServiceWorkerRegistration | null = null;
let visibilityHookInstalled = false;

function ensureVisibilityUpdateHook(): void {
  if (visibilityHookInstalled || typeof document === 'undefined') {
    return;
  }
  visibilityHookInstalled = true;
  visibilityManager.subscribe((isVisible) => {
    if (!isVisible || !activeRegistration) {
      return;
    }
    activeRegistration.update().catch((error) => {
      console.log('ServiceWorker update check failed:', error);
    });
  });
}

export function registerServiceWorker(scriptUrl = '/sw.js'): void {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return;
  }

  if (!('serviceWorker' in navigator)) {
    return;
  }

  ensureVisibilityUpdateHook();

  const register = () => {
    navigator.serviceWorker
      .register(scriptUrl)
      .then((registration) => {
        activeRegistration = registration;
        logServiceWorkerRegistration(registration);
      })
      .catch((error) => {
        console.log('ServiceWorker registration failed:', error);
      });
  };

  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }
}

/** @internal Test hook */
export function __setActiveServiceWorkerRegistrationForTests(
  registration: ServiceWorkerRegistration | null,
): void {
  activeRegistration = registration;
}
